import { PaginatedResponse } from './responseTypes';

/** RF band for a basic service set (Starlink setWifiConfig). */
export enum WifiRfBand {
  RF_2GHZ = 'RF_2GHZ',
  RF_5GHZ = 'RF_5GHZ',
  RF_5GHZ_HIGH = 'RF_5GHZ_HIGH',
}

/** Wifi RF Band Values */
export const WIFI_RF_BAND_VALUES = {
  [WifiRfBand.RF_2GHZ]: '2.4 GHz',
  [WifiRfBand.RF_5GHZ]: '5 GHz',
  [WifiRfBand.RF_5GHZ_HIGH]: '5 GHz High',
} as const;

export enum WifiAuthType {
  WPA2 = 'WPA2',
  WPA3 = 'WPA3',
  WPA2_WPA3 = 'WPA2_WPA3',
  WPA3_OWE = 'WPA3_OWE',
  OPEN = 'OPEN',
}

export interface WifiAuthPassword {
  password: string;
}

export interface WifiAuthEmpty {
  [key: string]: never;
}

export interface BasicServiceSet {
  ssid: string;
  band: WifiRfBand;
  hidden?: boolean;
  authWpa2?: WifiAuthPassword | null;
  authWpa3?: WifiAuthPassword | null;
  authWpa2Wpa3?: WifiAuthPassword | null;
  authOpenEncrypted?: WifiAuthEmpty | null;
  authOpen?: WifiAuthEmpty | null;
}

export function getBssAuthType(bss: BasicServiceSet): WifiAuthType {
  if (bss.authOpenEncrypted != null) return WifiAuthType.WPA3_OWE;
  if (bss.authOpen != null) return WifiAuthType.OPEN;
  if (bss.authWpa2Wpa3 != null) return WifiAuthType.WPA2_WPA3;
  if (bss.authWpa3 != null) return WifiAuthType.WPA3;
  return WifiAuthType.WPA2;
}

export function getBssPassword(bss: BasicServiceSet): string {
  return bss.authWpa2?.password ?? bss.authWpa3?.password ?? bss.authWpa2Wpa3?.password ?? '';
}

export function buildBssAuth(
  type: WifiAuthType,
  password?: string,
): Pick<BasicServiceSet, 'authWpa2' | 'authWpa3' | 'authWpa2Wpa3' | 'authOpenEncrypted' | 'authOpen'> {
  const cleared = {
    authWpa2: undefined,
    authWpa3: undefined,
    authWpa2Wpa3: undefined,
    authOpenEncrypted: undefined,
    authOpen: undefined,
  };

  switch (type) {
    case WifiAuthType.WPA3:
      return { ...cleared, authWpa3: { password: password ?? '' } };
    case WifiAuthType.WPA2_WPA3:
      return { ...cleared, authWpa2Wpa3: { password: password ?? '' } };
    case WifiAuthType.WPA3_OWE:
      return { ...cleared, authOpenEncrypted: {} };
    case WifiAuthType.OPEN:
      return { ...cleared, authOpen: {} };
    case WifiAuthType.WPA2:
    default:
      return { ...cleared, authWpa2: { password: password ?? '' } };
  }
}

export function sanitizeBasicServiceSet(bss: BasicServiceSet): BasicServiceSet {
  const type = getBssAuthType(bss);
  const password = getBssPassword(bss);
  const sanitized: BasicServiceSet = {
    ssid: bss.ssid,
    band: bss.band,
    ...buildBssAuth(type, password),
  };
  if (bss.hidden) sanitized.hidden = true;
  return sanitized;
}

export function sanitizeRouterConfigSettings(settings: RouterConfigSettings): RouterConfigSettings {
  const base = createDefaultRouterConfigSettings();
  return {
    ...base,
    ...settings,
    networks: settings.networks.map((network) => ({
      ...network,
      basicServiceSets: network.basicServiceSets.map(sanitizeBasicServiceSet),
    })),
  };
}

export function authTypeRequiresPassword(type: WifiAuthType): boolean {
  return type === WifiAuthType.WPA2 || type === WifiAuthType.WPA3 || type === WifiAuthType.WPA2_WPA3;
}

export interface WifiNetwork {
  basicServiceSets: BasicServiceSet[];
  clientIsolation: boolean;
}

/**
 * Starlink setWifiConfig payload stored as `RouterConfig.routerConfigJson`.
 * Value fields hold the setting; paired `apply*` flags control whether each is pushed.
 */
export interface RouterConfigSettings {
  applyPinCountryCode: boolean;
  setupComplete: boolean;
  applySetupComplete: boolean;
  applyChannel2ghz: boolean;
  applyChannel5ghz: boolean;
  applyChannel5ghzHigh: boolean;
  bypassMode: boolean;
  applyBypassMode: boolean;
  dfsEnabled: boolean;
  applyDfsEnabled: boolean;
  applyDisableMeshOnboarding: boolean;
  applyDisableWirelessMeshOnboarding: boolean;
  applyHttpServer: boolean;
  networks: WifiNetwork[];
  applyNetworks: boolean;
  applyHtBandwidth2ghz: boolean;
  applyHtBandwidth5ghz: boolean;
  applyVhtBandwidth: boolean;
  outdoorMode: boolean;
  applyOutdoorMode: boolean;
  disable2ghz: boolean;
  applyDisable2ghz: boolean;
  disable5ghz: boolean;
  applyDisable5ghz: boolean;
  disable5ghzHigh: boolean;
  applyDisable5ghzHigh: boolean;
  disablePendingUpdateReboot: boolean;
  applyDisablePendingUpdateReboot: boolean;
  disableSetWifiConfigFromController: boolean;
  applyDisableSetWifiConfigFromController: boolean;
  applyOnlyOverflightCountries: boolean;
  applyUnbridgedEthPorts: boolean;
  foreflightEnabled: boolean;
  applyForeflightEnabled: boolean;
}

export function createDefaultRouterConfigSettings(): RouterConfigSettings {
  return {
    applyPinCountryCode: true,
    setupComplete: true,
    applySetupComplete: true,
    applyChannel2ghz: true,
    applyChannel5ghz: true,
    applyChannel5ghzHigh: true,
    bypassMode: false,
    applyBypassMode: true,
    dfsEnabled: false,
    applyDfsEnabled: true,
    applyDisableMeshOnboarding: true,
    applyDisableWirelessMeshOnboarding: true,
    applyHttpServer: true,
    networks: [],
    applyNetworks: true,
    applyHtBandwidth2ghz: true,
    applyHtBandwidth5ghz: true,
    applyVhtBandwidth: true,
    outdoorMode: false,
    applyOutdoorMode: true,
    disable2ghz: false,
    applyDisable2ghz: true,
    disable5ghz: false,
    applyDisable5ghz: true,
    disable5ghzHigh: false,
    applyDisable5ghzHigh: true,
    disablePendingUpdateReboot: false,
    applyDisablePendingUpdateReboot: true,
    disableSetWifiConfigFromController: false,
    applyDisableSetWifiConfigFromController: true,
    applyOnlyOverflightCountries: true,
    applyUnbridgedEthPorts: true,
    foreflightEnabled: false,
    applyForeflightEnabled: true,
  };
}

/** Maps removed legacy keys to their Starlink `apply*` counterparts. */
const LEGACY_APPLY_KEY_MAP: Record<string, keyof RouterConfigSettings> = {
  pinCountryCode: 'applyPinCountryCode',
  channel2ghz: 'applyChannel2ghz',
  channel5ghz: 'applyChannel5ghz',
  channel5ghzHigh: 'applyChannel5ghzHigh',
  disableMeshOnboarding: 'applyDisableMeshOnboarding',
  disableWirelessMeshOnboarding: 'applyDisableWirelessMeshOnboarding',
  httpServer: 'applyHttpServer',
  networksEnabled: 'applyNetworks',
  htBandwidth2ghz: 'applyHtBandwidth2ghz',
  htBandwidth5ghz: 'applyHtBandwidth5ghz',
  vhtBandwidth: 'applyVhtBandwidth',
  onlyOverflightCountries: 'applyOnlyOverflightCountries',
  unbridgedEthPorts: 'applyUnbridgedEthPorts',
};

const DROPPED_LEGACY_KEYS = ['assetClass'] as const;

function normalizeRouterConfigSettings(raw: Record<string, unknown>): RouterConfigSettings {
  const normalized: Record<string, unknown> = {
    ...createDefaultRouterConfigSettings(),
    ...raw,
  };

  for (const [legacy, modern] of Object.entries(LEGACY_APPLY_KEY_MAP)) {
    if (legacy in raw && !(modern in raw)) {
      normalized[modern] = raw[legacy];
    }
    delete normalized[legacy];
  }

  for (const key of DROPPED_LEGACY_KEYS) {
    delete normalized[key];
  }

  return normalized as unknown as RouterConfigSettings;
}

/** Portal-stored reusable config (metadata + Starlink settings JSON). */
export interface RouterConfig {
  configId: string;
  nickname: string;
  routerConfigJson: string;
}

export interface CreateRouterConfigData {
  nickname: string;
  routerConfigJson: string;
}

export interface ApplyRouterConfigRequest {
  routerIds: string[];
  configId: string | undefined;
}

export function parseRouterConfigSettings(json: string | null | undefined): RouterConfigSettings | null {
  if (!json) return null;
  try {
    return normalizeRouterConfigSettings(JSON.parse(json) as Record<string, unknown>);
  } catch {
    return null;
  }
}
